import React, {useContext, useEffect, useState} from 'react'
import {Button, Form, Input, Select, Space} from 'antd'
import {
    BankOutlined,
    CheckCircleOutlined,
    CreditCardOutlined,
    FileProtectOutlined,
    ProjectOutlined,
    UserAddOutlined,
    UserSwitchOutlined
} from '@ant-design/icons'
import toast from 'react-hot-toast'
import PageBuilder from '../../../components/panel/PageBuilder'
import {
    DealerFilter, DealerInfoType, DealerPayInfo,
    DealerType,
    useApproveDealerMutation, useChangeDealerPayMutation,
    useChangeDealerStatusMutation,
    useCreateDealerMutation,
    useGetDealersQuery
} from '../../../api/dealerApi'
import {ModalConfirmContext} from '../../../providers/ModalConfirmProvider'
import {useAppSelector} from '../../../hooks'
import UserChangeStatusModal from '../UserChangeStatusModal'
import {useNavigate} from 'react-router-dom'
import ExtendedTable from "../../../components/ExtendedTable"
import ModalWithForm from "../ModalWithForm"
import DealerForm from "./DealerForm"
import {UserStatusInfo} from "../../../api/accountManagerApi"
import {statusFilterOptions} from "../accountManager/AccountManagers";
import {useFilterTable} from "../../../hooks/useFilterTable";

const Dealers = () => {
    const {role} = useAppSelector(state => state.user)
    const {showModal} = useContext(ModalConfirmContext)
    const navigate = useNavigate()

    const [formCreate] = Form.useForm<DealerInfoType>()
    const [formChangeStatus] = Form.useForm<UserStatusInfo>()
    const [formChangePay] = Form.useForm<DealerPayInfo>()

    const [isModalOpenChangeStatus, setIsModalOpenChangeStatus] = useState(false)
    const [isModalOpenChangePay, setIsModalOpenChangePay] = useState(false)
    const [isModalOpenCreate, setIsModalOpenCreate] = useState(false)

    const [create, resultsCreate] = useCreateDealerMutation()
    const [changeStatus, resultsChangeStatus] = useChangeDealerStatusMutation()
    const [changePay, resultsChangePay] = useChangeDealerPayMutation()
    const [approve] = useApproveDealerMutation()

    const [filter, setFilter] = useState<DealerFilter>({
        page: 1,
        name: '',
        lastname: '',
        email: '',
        status: null,
    })

    const {data, isLoading, refetch} = useGetDealersQuery(filter)

    const [
        filterByText,
        _,
        filterBySelect,
    ] = useFilterTable<DealerFilter>(filter, setFilter);

    const columns = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ...filterByText('email')
        },
        {
            title: 'First Name',
            dataIndex: 'name',
            key: 'name',
            ...filterByText('name')
        },
        {
            title: 'Last Name',
            dataIndex: 'lastname',
            key: 'lastname',
            ...filterByText('lastname')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...filterBySelect('status', statusFilterOptions, 'id', false)
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_: void, user: DealerType) => (
                <>
                    {role === 'admin' && (
                        <>
                            {
                                user.status !== 'sign_up_email_confirmation' && (
                                    <Button
                                        onClick={() => {
                                            const {status, id} = user
                                            formChangeStatus.setFieldsValue({status, id})
                                            setIsModalOpenChangeStatus(true)
                                        }}
                                        type="link"
                                        size="small"
                                    >
                                        <UserSwitchOutlined/>
                                    </Button>
                                )
                            }

                            {
                                user.status === 'wait_approving' && (
                                    <Button
                                        onClick={() => {
                                            const handleApprove = async () => {
                                                const {id} = user
                                                const loader = toast.loading("Progressing...");
                                                const res = await approve({id})
                                                toast.dismiss(loader);
                                                if ('error' in res) return
                                                toast.success('Dealer is successfully approved!')
                                                refetch()
                                            }

                                            showModal('approve the dealer', handleApprove)
                                        }}
                                        type="link"
                                        size="small"
                                    >
                                        <CheckCircleOutlined/>
                                    </Button>
                                )}
                        </>
                    )}

                    {user.status === 'active' && (
                        <>
                            {
                                (role === 'admin' || role === 'account manager') && (
                                    <Button
                                        onClick={() => {
                                            const {id} = user
                                            navigate(`/licenses/dealer/${id}`)
                                        }}
                                        type="link"
                                        size="small">
                                        <FileProtectOutlined/>
                                    </Button>
                                )
                            }

                            {
                                role === 'account manager' && <>
                                    {
                                        user.dealerInfo.type_pay !== 0 && <Button
                                            onClick={() => {
                                                const {id} = user
                                                navigate(`/apo/dealer/${id}`)
                                            }}
                                            type="link"
                                            size="small">
                                            <ProjectOutlined/>
                                        </Button>
                                    }

                                    <Button
                                        onClick={() => {
                                            const {dealerInfo, id} = user
                                            formChangePay.setFieldsValue({type_pay: dealerInfo?.type_pay, id})
                                            setIsModalOpenChangePay(true)
                                        }}
                                        type="link"
                                        size="small"
                                    >
                                        <BankOutlined/>
                                    </Button>

                                    <Button
                                        onClick={() => {
                                            const {id} = user
                                            navigate(`/invoices/dealer/${id}`)
                                        }}
                                        type="link"
                                        size="small">
                                        <CreditCardOutlined/>
                                    </Button>
                                </>
                            }
                        </>
                    )}
                </>
            )
        }
    ]

    useEffect(() => {
        refetch()
    }, [filter])

    return (
        <PageBuilder title="Dealers" documentTitle="Dealers">
            {
                role === "admin" && <Space style={{marginBottom: 20}}>
                    <Button type="primary" onClick={() => setIsModalOpenCreate(true)}>
                        <UserAddOutlined/> Create Dealer
                    </Button>
                </Space>
            }

            <ExtendedTable
                data={data}
                isLoading={isLoading}
                dataKey="items"
                columns={columns}
                onChange={e => setFilter(prevState => ({...prevState, page: e}))}
            />

            <ModalWithForm
                title={"Create Dealer"}
                isOpen={isModalOpenCreate}
                setIsOpen={setIsModalOpenCreate}
                isLoading={resultsCreate.isLoading}
                submit={formCreate.submit}
            >
                <DealerForm
                    form={formCreate}
                    onFinish={create}
                    onSubmitted={() => {
                        setIsModalOpenCreate(false)
                        refetch()
                        formCreate.resetFields()
                    }}
                    mode='creating'
                />
            </ModalWithForm>

            <UserChangeStatusModal
                form={formChangeStatus}
                changeStatus={changeStatus}
                isModalOpen={isModalOpenChangeStatus}
                setIsModalOpen={setIsModalOpenChangeStatus}
                isLoading={resultsChangeStatus.isLoading}
                refetch={refetch}
            />

            <ModalWithForm
                title={"Change Pay type"}
                isOpen={isModalOpenChangePay}
                setIsOpen={setIsModalOpenChangePay}
                isLoading={resultsChangePay.isLoading}
                submit={formChangePay.submit}
            >
                <Form
                    validateMessages={{required: "Field is required!"}}
                    layout="vertical"
                    form={formChangePay}
                    onFinish={async (e) => {
                        const loader = toast.loading("Progressing...");
                        const res = await changePay(e)
                        toast.dismiss(loader);
                        if ('error' in res) return
                        toast.success("The Pay type is successfully changed!")
                        setIsModalOpenChangePay(false)
                        refetch()
                        formChangePay.resetFields()
                    }}
                >
                    <Form.Item name="id" noStyle>
                        <Input type="hidden"/>
                    </Form.Item>

                    <Form.Item rules={[{required: true}]} label="Type" name="type_pay">
                        <Select placeholder="Pay type" options={initOptionsPayType}/>
                    </Form.Item>
                </Form>
            </ModalWithForm>
        </PageBuilder>
    )
}

export default Dealers

const initOptionsPayType: { value: number, label: string }[] = [
    {value: 0, label: 'Pay for licenses'},
    {value: 1, label: 'Pay for subscription'},
]
